import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { HeaderTabsService } from '../../services/header-tabs.service';
import { SignalRService } from '../../services/signalr.service';
import { UserRoleService } from '../../services/user-role.service';

@Component({
    selector: 'app-header',
    standalone: true,
    templateUrl: './header.component.html',
    imports: [CommonModule, RouterLink]
})
export class HeaderComponent implements OnInit, OnDestroy {
    readonly breadcrumbService = inject(BreadcrumbService);
    readonly headerTabs = inject(HeaderTabsService);
    readonly signalR = inject(SignalRService);
    isIframe = false;
    loginDisplay = false;
    accountInfo: AccountInfo | null = null;
    photo: SafeUrl | null = null;
    public UserRoles = inject(UserRoleService);

    private readonly _destroying$ = new Subject<void>();

    constructor(
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private http: HttpClient,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.isIframe = window !== window.parent && !window.opener;

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.setLoginDisplay();
                if (this.accountInfo) {
                    this.getUserPhoto().subscribe({
                        next: (photo) => this.photo = photo,
                        error: () => this.photo = null
                    });
                }
            });

        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.setLoginDisplay();
            });

        this.setLoginDisplay();
    }

    setLoginDisplay() {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
        this.accountInfo = this.authService.instance.getActiveAccount();
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    getUserPhoto(): Observable<SafeUrl> {
        const requestUrl = 'https://graph.microsoft.com/v1.0/me/photo/$value';
        return new Observable<SafeUrl>(observer => {
            this.authService.acquireTokenSilent({
                scopes: ['User.Read']
            }).subscribe({
                next: (result: AuthenticationResult) => {
                    const headers = { Authorization: `Bearer ${result.accessToken}` };
                    this.http.get(requestUrl, { responseType: 'blob', headers }).subscribe({
                        next: (photoBlob) => {
                            const url = window.URL.createObjectURL(photoBlob);
                            observer.next(this.sanitizer.bypassSecurityTrustUrl(url));
                            observer.complete();
                        },
                        error: (err) => {
                            observer.error(err);
                        }
                    });
                },
                error: (err) => {
                    observer.error(err);
                }
            });
        });
    }
}
